import React, { useState } from 'react';
import { Box, Stepper, Step, StepLabel, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import WelcomeStep from './steps/WelcomeStep';
import BudgetStep from './steps/BudgetStep';
import StrategyStep from './steps/StrategyStep';
import SummaryStep from './steps/SummaryStep';
import { paperTradingApi } from '../../services/paperTradingApi';

const steps = ['Willkommen', 'Budget', 'Strategie', 'Zusammenfassung'];

const OnboardingWizard: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Onboarding state
  const [budget, setBudget] = useState(10000);
  const [strategyType, setStrategyType] = useState('marktmeister');
  const [market, setMarket] = useState('BTC-EUR');
  const [amountPerTrade, setAmountPerTrade] = useState(50);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleComplete = async () => {
    setLoading(true);
    try {
      const stored = localStorage.getItem('authTokens');
      const token = stored ? JSON.parse(stored).access_token : null;
      console.log('Token available:', !!token, 'Token start:', token?.substring(0, 20));

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/paper/onboarding/complete`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            total_budget_eur: budget,
            strategy_type: strategyType,
            strategy_name: `Meine ${
              { marktmeister: 'MarktMeister' }[strategyType] || strategyType
            } Strategie`,
            market,
            amount_per_trade: amountPerTrade,
          }),
        }
      );

      const data = await response.json();
      console.log('Onboarding response:', response.status, data);

      if (!response.ok) {
        throw new Error(data.detail || `HTTP ${response.status}`);
      }

      navigate('/dashboard');
    } catch (error: any) {
      console.error('Onboarding failed:', error);
      enqueueSnackbar('Onboarding fehlgeschlagen: ' + (error?.message || 'Unbekannter Fehler'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && <WelcomeStep onNext={handleNext} />}
      {activeStep === 1 && (
        <BudgetStep budget={budget} onBudgetChange={setBudget} onNext={handleNext} onBack={handleBack} />
      )}
      {activeStep === 2 && (
        <StrategyStep
          strategyType={strategyType}
          market={market}
          amountPerTrade={amountPerTrade}
          onStrategyTypeChange={setStrategyType}
          onMarketChange={setMarket}
          onAmountChange={setAmountPerTrade}
          onNext={handleNext}
          onBack={handleBack}
        />
      )}
      {activeStep === 3 && (
        <SummaryStep
          budget={budget}
          strategyType={strategyType}
          market={market}
          amountPerTrade={amountPerTrade}
          onBack={handleBack}
          onComplete={handleComplete}
          loading={loading}
        />
      )}
    </Container>
  );
};

export default OnboardingWizard;
